import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Eye, EyeOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function RegisterPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", password: "", password2: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    try {
      const lead = JSON.parse(sessionStorage.getItem("gp_lead") || "null");
      if (lead) setForm((f) => ({ ...f, name: lead.name || "", company: lead.company || "", email: lead.email || "", phone: lead.phone || "" }));
    } catch {}
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("Şifre en az 6 karakter olmalıdır");
    if (form.password !== form.password2) return toast.error("Şifreler eşleşmiyor");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", {
        name: form.name, company: form.company, email: form.email, phone: form.phone, password: form.password,
      });
      login(data.token, data.user);
      toast.success("Hesabınız oluşturuldu! Şimdi projenizi oluşturun.");
      navigate("/musteri/proje-olustur");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div className="space-y-1.5">
      <Label htmlFor={`r-${key}`}>{label}</Label>
      <Input id={`r-${key}`} data-testid={`register-${key}`} value={form[key]} onChange={set(key)} className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" {...props} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 transition-colors duration-200 mb-6" data-testid="register-back-home">
          <ArrowLeft size={15} /> Ana Sayfa
        </Link>
        <div className="bg-white border border-slate-200 p-8 lg:p-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">GettProposal</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tighter mt-1">Hesap Oluşturun</h1>
          <p className="text-sm text-slate-500 mt-2">Kayıt olun, projenizi oluşturun ve tedarikçi tekliflerini tek ekranda karşılaştırın.</p>
          <form onSubmit={submit} className="space-y-4 mt-8">
            {field("name", "Ad Soyad *", { required: true, placeholder: "Adınız Soyadınız" })}
            {field("company", "Firma *", { required: true, placeholder: "Firma adı" })}
            {field("email", "E-posta *", { required: true, type: "email", placeholder: "ornek@firma.com" })}
            {field("phone", "Telefon", { placeholder: "+90 5xx xxx xx xx" })}
            <div className="space-y-1.5">
              <Label htmlFor="r-password">Şifre *</Label>
              <div className="relative">
                <Input id="r-password" data-testid="register-password" type={show ? "text" : "password"} value={form.password} onChange={set("password")} required minLength={6} placeholder="En az 6 karakter" className="rounded-none bg-[#F5F7FA] border-slate-200 h-11 pr-10" />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-900" data-testid="register-password-toggle">
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="r-password2">Şifre (tekrar) *</Label>
              <Input id="r-password2" data-testid="register-password2" type={show ? "text" : "password"} value={form.password2} onChange={set("password2")} required placeholder="Şifrenizi tekrar girin" className="rounded-none bg-[#F5F7FA] border-slate-200 h-11" />
            </div>
            <Button type="submit" disabled={busy} data-testid="register-submit" className="w-full rounded-none h-12 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
              {busy ? "Oluşturuluyor..." : "Kayıt Ol ve Devam Et"} <ArrowRight size={15} />
            </Button>
          </form>
          <p className="text-sm text-slate-500 mt-6">
            Zaten hesabınız var mı?{" "}
            <Link to="/login" className="text-slate-900 font-semibold hover:underline" data-testid="register-login-link">Giriş yapın</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
